import { useEffect, useState } from "react";
import { api, ROLE_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { SlidersHorizontal, Loader2, Save } from "lucide-react";
import { invalidatePermissions } from "@/lib/perms";

// An admin decides, per role, which sections that role can open — before anyone is invited.
export default function RolePresets({ academyId }) {
  const [d, setD] = useState(null);
  const [access, setAccess] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!academyId) return;
    api.get(`/academies/${academyId}/role-access`).then((r) => { setD(r.data); setAccess(r.data.access); })
      .catch(() => setD(null));
  }, [academyId]);

  const toggle = (role, sec) =>
    setAccess((a) => ({ ...a, [role]: { ...a[role], [sec]: !a[role]?.[sec] } }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/academies/${academyId}/role-access`, { access });
      setAccess(data.access);
      invalidatePermissions();
      toast.success("Role access saved", { description: "Applies to everyone in this academy right away" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const reset = () => setAccess(JSON.parse(JSON.stringify(d.defaults)));

  if (!d || !d.editable) return null;

  return (
    <div className="border border-border rounded-xl mb-6" data-testid="role-presets">
      <div className="px-4 py-2.5 border-b border-border flex items-center gap-2 flex-wrap">
        <SlidersHorizontal className="w-3.5 h-3.5" />
        <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">
          What each role can open
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="role-presets-reset" onClick={reset}
            className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-surface transition-colors">Reset to defaults</button>
          <button data-testid="role-presets-save" onClick={save} disabled={busy}
            className="text-xs px-3 py-1.5 rounded-full bg-foreground text-background hover:bg-neutral-800 transition-colors flex items-center gap-1.5 disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
            <tr>
              <th className="text-left px-4 py-2">Role</th>
              {d.sections.map((s) => <th key={s} className="px-3 py-2 whitespace-nowrap">{d.labels[s]}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {d.roles.map((role) => (
              <tr key={role} data-testid={`role-row-${role}`}>
                <td className="px-4 py-2 whitespace-nowrap font-medium">{ROLE_LABELS[role] || role}</td>
                {d.sections.map((s) => (
                  <td key={s} className="px-3 py-2 text-center">
                    <input type="checkbox" checked={!!access[role]?.[s]} onChange={() => toggle(role, s)}
                      data-testid={`role-${role}-${s}`} aria-label={`${role} ${s}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="px-4 py-2.5 text-[11px] text-muted-foreground border-t border-border">
        Admins can view everything. Athletes can only view their own records. Coaches can view everything about the
        athletes assigned to them, except archived clinical records.
      </p>
    </div>
  );
}
